@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginekalender

import kotlin.math.abs

import org.example.project.utils.All
import org.example.project.utils.LocalizationManager

fun parseKalenderIsoDate(value: String): All? {
    val parts = value.split("-")
    if (parts.size != 3) return null
    val year = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return All(
        datum = value,
        year = year,
        month = month,
        day = day
    )
}

private fun kalenderYear(date: All): Int = date.year
private fun kalenderMonth(date: All): Int = date.month
private fun kalenderDay(date: All): Int = date.day

fun kalenderLocalized(key: String, fallback: String): String =
    LocalizationManager.get(key).takeUnless { it == "[$key]" } ?: fallback

fun kalenderTodayIsoDate(): String {
    val now = js("new Date()")
    val year = now.getFullYear() as Int
    val month = (now.getMonth() as Int) + 1
    val day = now.getDate() as Int
    return "${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
}

fun kalenderWeekdayOf(year: Int, month: Int, day: Int): Int {
    val jsDate = js("new Date(year, month - 1, day)")
    val weekday = jsDate.getDay() as Int
    return if (weekday == 0) 6 else weekday - 1
}

fun kalenderDaysInMonth(year: Int, month: Int): Int {
    val jsDate = js("new Date(year, month, 0)")
    return jsDate.getDate() as Int
}

fun kalenderMonthName(month: Int): String = listOf(
    "calendar_month_january" to "Januar",
    "calendar_month_february" to "Februar",
    "calendar_month_march" to "März",
    "calendar_month_april" to "April",
    "calendar_month_may" to "Mai",
    "calendar_month_june" to "Juni",
    "calendar_month_july" to "Juli",
    "calendar_month_august" to "August",
    "calendar_month_september" to "September",
    "calendar_month_october" to "Oktober",
    "calendar_month_november" to "November",
    "calendar_month_december" to "Dezember"
)[month - 1]
    .let { (key, fallback) -> kalenderLocalized(key, fallback) }

fun kalenderMonthShortName(month: Int): String = listOf(
    "calendar_month_january_short" to "Jan",
    "calendar_month_february_short" to "Feb",
    "calendar_month_march_short" to "Mär",
    "calendar_month_april_short" to "Apr",
    "calendar_month_may_short" to "Mai",
    "calendar_month_june_short" to "Jun",
    "calendar_month_july_short" to "Jul",
    "calendar_month_august_short" to "Aug",
    "calendar_month_september_short" to "Sep",
    "calendar_month_october_short" to "Okt",
    "calendar_month_november_short" to "Nov",
    "calendar_month_december_short" to "Dez"
)[month - 1]
    .let { (key, fallback) -> kalenderLocalized(key, fallback) }

fun kalenderWeekdayShortName(index: Int): String =
    listOf(
        "calendar_weekday_monday_short" to "Mo",
        "calendar_weekday_tuesday_short" to "Di",
        "calendar_weekday_wednesday_short" to "Mi",
        "calendar_weekday_thursday_short" to "Do",
        "calendar_weekday_friday_short" to "Fr",
        "calendar_weekday_saturday_short" to "Sa",
        "calendar_weekday_sunday_short" to "So"
    )[index].let { (key, fallback) -> kalenderLocalized(key, fallback) }

fun kalenderAddMonth(year: Int, month: Int, delta: Int): String {
    val absolute = (year * 12 + (month - 1)) + delta
    val nextYear = absolute / 12
    val nextMonth = (absolute % 12) + 1
    return "${nextYear.toString().padStart(4, '0')}-${nextMonth.toString().padStart(2, '0')}-01"
}

fun kalenderAddDays(year: Int, month: Int, day: Int, delta: Int): String {
    val jsDate = js("new Date(year, month - 1, day)")
    jsDate.setDate((jsDate.getDate() as Int) + delta)
    val nextYear = jsDate.getFullYear() as Int
    val nextMonth = (jsDate.getMonth() as Int) + 1
    val nextDay = jsDate.getDate() as Int
    return "${nextYear.toString().padStart(4, '0')}-${nextMonth.toString().padStart(2, '0')}-${nextDay.toString().padStart(2, '0')}"
}

fun kalenderWeekSwipeDirection(startX: Double, startY: Double, endX: Double, endY: Double): Int? {
    val deltaX = endX - startX
    val deltaY = endY - startY
    if (abs(deltaX) < 70.0 || abs(deltaX) < abs(deltaY) * 1.25) return null
    return if (deltaX < 0) 1 else -1
}

fun kalenderStartOfWeek(date: All): All {
    val offset = kalenderWeekdayOf(kalenderYear(date), kalenderMonth(date), kalenderDay(date))
    return parseKalenderIsoDate(
        kalenderAddDays(kalenderYear(date), kalenderMonth(date), kalenderDay(date), -offset)
    )!!
}

fun kalenderIsoWeekNumber(value: String): Int? {
    val parsed = parseKalenderIsoDate(value) ?: return null
    val year = parsed.year
    val month = parsed.month
    val day = parsed.day
    val utcDate = js("new Date(Date.UTC(year, month - 1, day))")
    val weekday = (utcDate.getUTCDay() as Int).let { if (it == 0) 7 else it }
    utcDate.setUTCDate((utcDate.getUTCDate() as Int) + 4 - weekday)
    val weekYear = utcDate.getUTCFullYear() as Int
    val yearStart = js("new Date(Date.UTC(weekYear, 0, 1))")
    val daysFromYearStart = ((utcDate.getTime() as Double) - (yearStart.getTime() as Double)) / 86_400_000.0
    return daysFromYearStart.toInt() / 7 + 1
}

fun buildKalenderMonthData(selectedDate: String, monthCursorIso: String): All {
    val fallbackIso = kalenderTodayIsoDate()
    val current = parseKalenderIsoDate(monthCursorIso)
        ?.let { parseKalenderIsoDate("${kalenderYear(it).toString().padStart(4, '0')}-${kalenderMonth(it).toString().padStart(2, '0')}-01") }
        ?: parseKalenderIsoDate(fallbackIso)
            ?.let { parseKalenderIsoDate("${kalenderYear(it).toString().padStart(4, '0')}-${kalenderMonth(it).toString().padStart(2, '0')}-01") }!!
    val selectedIso = selectedDate.takeIf { it.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) } ?: fallbackIso
    val monthStartWeekday = kalenderWeekdayOf(kalenderYear(current), kalenderMonth(current), 1)
    val currentMonthDays = kalenderDaysInMonth(kalenderYear(current), kalenderMonth(current))
    val previousMonthIso = kalenderAddMonth(kalenderYear(current), kalenderMonth(current), -1)
    val previousMonth = parseKalenderIsoDate(previousMonthIso)!!
    val nextMonthIso = kalenderAddMonth(kalenderYear(current), kalenderMonth(current), 1)
    val nextMonth = parseKalenderIsoDate(nextMonthIso)!!
    val previousMonthDays = kalenderDaysInMonth(kalenderYear(previousMonth), kalenderMonth(previousMonth))

    val cells = buildList {
        repeat(monthStartWeekday) { index ->
            val day = previousMonthDays - monthStartWeekday + index + 1
            add(
                All(
                    label = day.toString(),
                    datum = "${kalenderYear(previousMonth).toString().padStart(4, '0')}-${kalenderMonth(previousMonth).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}",
                    aktiv = false
                )
            )
        }
        (1..currentMonthDays).forEach { day ->
            add(
                All(
                    label = day.toString(),
                    datum = "${kalenderYear(current).toString().padStart(4, '0')}-${kalenderMonth(current).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}",
                    aktiv = true
                )
            )
        }
        val trailingDays = (42 - size).coerceAtLeast(0)
        repeat(trailingDays) { index ->
            val day = index + 1
            add(
                All(
                    label = day.toString(),
                    datum = "${kalenderYear(nextMonth).toString().padStart(4, '0')}-${kalenderMonth(nextMonth).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}",
                    aktiv = false
                )
            )
        }
    }

    return All(
        datum = "${kalenderYear(current).toString().padStart(4, '0')}-${kalenderMonth(current).toString().padStart(2, '0')}-01",
        bezeichnung = "${kalenderMonthName(kalenderMonth(current))} ${kalenderYear(current)}",
        previousMonthIso = previousMonthIso,
        nextMonthIso = nextMonthIso,
        module = cells,
        stufe = cells.indexOfFirst { it.datum == selectedIso }.takeIf { it >= 0 }?.let { it / 7 } ?: -1
    )
}

fun buildKalenderWeekDetailData(selectedDate: String): All {
    val selected = parseKalenderIsoDate(selectedDate) ?: parseKalenderIsoDate(kalenderTodayIsoDate())!!
    val weekStart = kalenderStartOfWeek(selected)
    val todayIso = kalenderTodayIsoDate()

    return All(
        module = (0..6).map { offset ->
            val iso = kalenderAddDays(kalenderYear(weekStart), kalenderMonth(weekStart), kalenderDay(weekStart), offset)
            val parsed = parseKalenderIsoDate(iso)!!
            All(
                datum = iso,
                label = kalenderWeekdayShortName(offset),
                bezeichnung = kalenderDay(parsed).toString(),
                kurzbezeichnung = kalenderMonthShortName(kalenderMonth(parsed)),
                aktiv = iso == todayIso
            )
        },
        tore = (0..23).toList()
    )
}
